import React from 'react';
import {Box, Chip, Typography, Button, Tooltip} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {RoleNames, UserRoleType, getHighestPriorityRole} from '@/config/roles';
import {appConfig} from '@/config/appConfig';
import './RoleSelection.css';

interface RoleSelectionProps {
    allRoles: UserRoleType[];
    activeRoles: UserRoleType[];
    hasActiveRole: (role: string) => boolean;
    toggleActiveRole: (role: UserRoleType) => void;
    resetActiveRoles: () => void;
    isAllRolesActive: () => boolean;
    getActiveRolesDisplay: () => string;
}

const RoleSelection: React.FC<RoleSelectionProps> = ({
    allRoles,
    activeRoles,
    hasActiveRole,
    toggleActiveRole,
    resetActiveRoles,
    isAllRolesActive,
    getActiveRolesDisplay,
}) => {
    const handleRoleChipClick = (role: UserRoleType, event: React.MouseEvent) => {
        event.stopPropagation();
        if (appConfig.activeRolesFeature.active) {
            toggleActiveRole(role);
        }
    };

    const handleResetRoles = (event: React.MouseEvent) => {
        event.stopPropagation();
        resetActiveRoles();
    };

    const isResetDisabled = () => {
        if (appConfig.activeRolesFeature.exclusiveMode) {
            const highest = getHighestPriorityRole(allRoles);
            return highest ? activeRoles.length === 1 && hasActiveRole(highest) : true;
        }
        return isAllRolesActive();
    };

    return (
        <Box className="roles-section">
            {appConfig.activeRolesFeature.active && (
                <Box className="roles-header">
                    <Box className="roles-header-content">
                        <Box className="roles-header-title">
                            <SecurityIcon fontSize="small" color="action"/>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                Application Roles
                            </Typography>
                        </Box>
                        <Tooltip
                            title={
                                appConfig.activeRolesFeature.exclusiveMode
                                    ? 'Reset to highest priority role'
                                    : 'Reset to all roles'
                            }
                        >
                            <span>
                                <Button
                                    size="small"
                                    startIcon={<RestartAltIcon fontSize="small"/>}
                                    onClick={handleResetRoles}
                                    disabled={isResetDisabled()}
                                    className="role-action-button"
                                >
                                    Reset
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {!appConfig.activeRolesFeature.active && (
                <Box className="roles-header-simple">
                    <SecurityIcon fontSize="small" color="action"/>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Application Roles
                    </Typography>
                </Box>
            )}

            <Box className="roles-container">
                {allRoles && allRoles.length > 0 ? (
                    allRoles.map(role => {
                        const isActive = hasActiveRole(role);
                        const isClickable = appConfig.activeRolesFeature.active;

                        return (
                            <Tooltip
                                key={role}
                                title={
                                    isClickable
                                        ? appConfig.activeRolesFeature.exclusiveMode
                                            ? isActive
                                                ? 'Currently active'
                                                : 'Click to switch'
                                            : isActive
                                                ? 'Click to deactivate'
                                                : 'Click to activate'
                                        : ''
                                }
                                arrow
                            >
                                <Chip
                                    label={RoleNames[role] ?? role}
                                    size="small"
                                    color={isActive ? 'primary' : 'default'}
                                    variant={isActive ? 'filled' : 'outlined'}
                                    onClick={isClickable ? e => handleRoleChipClick(role, e) : undefined}
                                    icon={
                                        isActive && isClickable && !appConfig.activeRolesFeature.exclusiveMode
                                            ? <CheckCircleIcon/>
                                            : undefined
                                    }
                                    className={`role-chip ${isActive ? 'role-chip-active' : 'role-chip-inactive'} ${isClickable ? 'role-chip-clickable' : 'role-chip-static'}`}
                                    clickable={isClickable}
                                />
                            </Tooltip>
                        );
                    })
                ) : (
                    <Typography variant="body2" color="text.secondary" className="roles-empty-message">
                        No application roles
                    </Typography>
                )}
            </Box>

            {appConfig.activeRolesFeature.active && !isAllRolesActive() && (
                <Box className="active-roles-status">
                    <Typography variant="caption" color="text.secondary">
                        Active: {getActiveRolesDisplay()}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default RoleSelection;
